import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, SlidersHorizontalIcon, XIcon } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductGrid } from "../components/ProductGrid";
import { FilterPanel } from "../components/shop/FilterPanel";
import { collections } from "../data/collections";
import { priceBuckets } from "../data/products";
import { useShopFilters, type SortOption } from "../hooks/useShopFilters";
import type { Category } from "../types/product";
import { btnPrimary, btnSecondary, container, ease } from "../utils/styles";
const categoryPills: {
  label: string;
  value: Category | null;
}[] = [{
  label: "Alles",
  value: null
}, {
  label: "Verlichting",
  value: "Verlichting"
}, {
  label: "Woondecoratie",
  value: "Woondecoratie"
}, {
  label: "Gepersonaliseerd",
  value: "Gepersonaliseerd"
}];
export function Shop() {
  const {
    filters,
    toggle,
    setPersonalizable,
    clearAll,
    clearQuery,
    query,
    sort,
    setSort,
    options,
    results,
    activeCount
  } = useShopFilters();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const single = filters.categories.length === 1 ? filters.categories[0] : null;
  const collection = collections.find(c => c.filter === single);
  const title = query ? `Zoekresultaten voor “${query}”` : single ?? "Alle producten";
  const description = query ? undefined : collection?.description ?? "Alle ontwerpen uit onze studio: lampen, woondecoratie en persoonlijke cadeaus.";
  const extraFilters = activeCount - filters.categories.length;
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);
  const chips = [...filters.types.map(v => ({
    key: `t-${v}`,
    label: v,
    onRemove: () => toggle("types", v)
  })), ...filters.prices.map(v => ({
    key: `p-${v}`,
    label: priceBuckets.find(b => b.id === v)?.label ?? v,
    onRemove: () => toggle("prices", v)
  })), ...filters.colors.map(v => ({
    key: `k-${v}`,
    label: v,
    onRemove: () => toggle("colors", v)
  })), ...(filters.personalizable ? [{
    key: "pers",
    label: "Personaliseerbaar",
    onRemove: () => setPersonalizable(false)
  }] : []), ...(query ? [{
    key: "q",
    label: `“${query}”`,
    onRemove: clearQuery
  }] : [])];
  const panel = <FilterPanel filters={filters} options={options} toggle={toggle} setPersonalizable={setPersonalizable} data-id="element-538" data-mp="21864n8g8svcm" />;
  return <>
      <section className="border-b border-line bg-sand" data-id="element-539" data-mp="1w6p79mwyvr0s">
        <div className={`${container} py-10 lg:py-14`} data-id="element-540" data-mp="2ay7uo5s5onu6">
          <Breadcrumbs items={[{
          label: "Home",
          to: "/"
        }, {
          label: "Shop",
          to: single || query ? "/shop" : undefined
        }, ...(single ? [{
          label: single
        }] : [])]} data-id="element-541" data-mp="266zk36f3awff" />
          <h1 className="mt-5 text-4xl font-medium tracking-[-0.04em] text-ink sm:text-5xl" data-id="element-542" data-mp="1ifakr63up7oz">{title}</h1>
          {description && <p className="mt-3 max-w-xl text-[15px] text-muted" data-id="element-543" data-mp="3p5dzodmxrbsu">{description}</p>}
          <div className="no-scrollbar -mx-5 mt-8 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:px-0" data-id="element-544" data-mp="3p3azx1vze4ax">
            {categoryPills.map(c => {
            const active = c.value === single || c.value === null && !single;
            return <button key={c.label} type="button" onClick={() => navigate(c.value ? `/shop?categorie=${c.value}` : "/shop")} className={`relative h-10 shrink-0 rounded-full px-5 text-sm transition-colors duration-200 ${active ? "text-paper" : "bg-paper text-ink hover:bg-stone"}`} data-id="element-545" data-mp="3k5j9kcaziq8u">
                  {active && <motion.span layoutId="shop-cat" className="absolute inset-0 rounded-full bg-ink" transition={{
                duration: 0.25,
                ease
              }} data-id="element-546" data-mp="7ontffitr0pa" />}
                  <span className="relative" data-id="element-547" data-mp="2negz7evz44vk">{c.label}</span>
                </button>;
          })}
          </div>
        </div>
      </section>

      <div className={`${container} grid gap-10 pb-20 pt-8 lg:grid-cols-[240px_1fr] lg:gap-14 lg:pb-28`} data-id="element-548" data-mp="37oi8yz6dgpmz">
        <aside aria-label="Filters" className="hidden lg:block" data-id="element-549" data-mp="32xjmtiypcxbt">
          <div className="sticky top-[96px]" data-id="element-550" data-mp="32w8i9gxfnljn">
            <div className="mb-5 flex items-center justify-between" data-id="element-551" data-mp="2y00awpph8wn1">
              <h2 className="text-[15px] font-medium text-ink" data-id="element-552" data-mp="2txn3zxyzusoo">Filters</h2>
              {extraFilters > 0 && <button type="button" onClick={clearAll} className="text-xs text-muted underline underline-offset-4 hover:text-ink" data-id="element-553" data-mp="3qo5sj3l8i5in">
                  Wis alles
                </button>}
            </div>
            {panel}
          </div>
        </aside>

        <div className="min-w-0" data-id="element-554" data-mp="3hosh45djv6va">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-555" data-mp="3coo08e8qca4j">
            <div className="flex items-center gap-3" data-id="element-556" data-mp="267j3hzh3r91w">
              <button type="button" onClick={() => setDrawerOpen(true)} className="inline-flex h-10 items-center gap-2 rounded-full border border-ink/15 px-4 text-sm text-ink transition-colors duration-150 hover:border-ink lg:hidden" data-id="element-557" data-mp="2693wvlrokly6">
                <SlidersHorizontalIcon className="h-4 w-4" data-id="element-558" data-mp="lprs4urjmr0i" />
                Filters
                {extraFilters > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1.5 text-[11px] text-paper" data-id="element-559" data-mp="prlmymf250sr">{extraFilters}</span>}
              </button>
              <p className="text-sm text-muted" data-id="element-560" data-mp="uk6chbqxkrqh">{results.length} producten</p>
            </div>
            <label className="relative inline-flex items-center gap-2" data-id="element-561" data-mp="zj9865l0tlls">
              <span className="hidden text-sm text-muted sm:inline" data-id="element-562" data-mp="14eyfww7728t6">Sorteer op</span>
              <select value={sort} onChange={e => setSort(e.target.value as SortOption)} className="h-10 appearance-none rounded-full border border-ink/15 bg-paper pl-4 pr-10 text-sm text-ink transition-colors duration-150 hover:border-ink focus:border-ink focus:outline-none" data-id="element-563" data-mp="19ge06m1jsbai">
                <option value="aanbevolen" data-id="element-564" data-mp="3fadoqoq18xso">Aanbevolen</option>
                <option value="prijs-op" data-id="element-565" data-mp="melt9gcpj128">Prijs: laag naar hoog</option>
                <option value="prijs-af" data-id="element-566" data-mp="hf96swpqzw06">Prijs: hoog naar laag</option>
                <option value="naam" data-id="element-567" data-mp="hh2hqo7e92dz">Naam A–Z</option>
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3.5 h-4 w-4 text-muted" data-id="element-568" data-mp="vftrjyf841wb" />
            </label>
          </div>

          <AnimatePresence initial={false} data-id="element-569" data-mp="15cewykv5nf0o">
            {chips.length > 0 && <motion.ul initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: "auto",
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease
          }} className="flex flex-wrap gap-2 overflow-hidden pt-4" data-id="element-570" data-mp="15abzjfroajlx">
                {chips.map(c => <li key={c.key} data-id="element-571" data-mp="1a3wzvegqnpoa">
                    <button type="button" onClick={c.onRemove} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-sand pl-3 pr-2 text-xs text-ink transition-colors duration-150 hover:bg-stone" aria-label={`Filter ${c.label} verwijderen`} data-id="element-572" data-mp="1a5rbu3a187g8">
                      {c.label}
                      <XIcon className="h-3.5 w-3.5" data-id="element-573" data-mp="22nsbis17l3ax" />
                    </button>
                  </li>)}
              </motion.ul>}
          </AnimatePresence>

          <div className="mt-8" data-id="element-574" data-mp="1tox7nmln05as">
            {results.length === 0 ? <div className="flex flex-col items-center rounded-xl bg-sand px-6 py-20 text-center" data-id="element-575" data-mp="1xph2ukmngyua">
                <p className="text-xl font-medium text-ink" data-id="element-576" data-mp="1jylqgycxzzly">Geen producten gevonden</p>
                <p className="mt-2 max-w-sm text-sm text-muted" data-id="element-577" data-mp="1or5fcrh2yh62">Probeer minder filters. Niet gevonden wat je zoekt? We maken het ook op maat.</p>
                <button type="button" onClick={clearAll} className={`${btnPrimary} mt-6`} data-id="element-578" data-mp="19z4oos0ta5v9">
                  Wis alle filters
                </button>
              </div> : <ProductGrid key={`${single}-${sort}-${activeCount}-${query}`} products={results} cols={3} data-id="element-579" data-mp="111b1q7oxsokr" />}
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-580" data-mp="1lhvsflj05213">
        {drawerOpen && <>
            <motion.div className="fixed inset-0 z-50 bg-ink/40 lg:hidden" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setDrawerOpen(false)} data-id="element-581" data-mp="1gij4exge9owx" />
            <motion.aside role="dialog" aria-modal="true" aria-label="Filters" className="fixed inset-y-0 right-0 z-50 flex w-[88%] max-w-sm flex-col bg-paper lg:hidden" initial={{
          x: "100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "100%"
        }} transition={{
          duration: 0.28,
          ease
        }} data-id="element-582" data-mp="17idx0nb7aqir">
              <div className="flex h-16 items-center justify-between border-b border-line px-5" data-id="element-583" data-mp="nudjoapcbzx3">
                <h2 className="text-lg font-medium text-ink" data-id="element-584" data-mp="jss066iawndx">Filters</h2>
                <button type="button" onClick={() => setDrawerOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-sand" aria-label="Sluiten" data-id="element-585" data-mp="ju3200s6rl2e">
                  <XIcon className="h-5 w-5" data-id="element-586" data-mp="2qg96rc6o87yi" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 pt-5" data-id="element-587" data-mp="2ve2ck442azy9">{panel}</div>
              <div className="grid grid-cols-2 gap-2 border-t border-line p-4" data-id="element-588" data-mp="2zia1o2efmhx0">
                <button type="button" onClick={clearAll} className={btnSecondary} data-id="element-589" data-mp="34gtl9w5jzhm7">
                  Wis alles
                </button>
                <button type="button" onClick={() => setDrawerOpen(false)} className={btnPrimary} data-id="element-590" data-mp="27rm38y457qn1">
                  Toon {results.length}
                </button>
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>
    </>;
}